import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import DropdownCombo, {
  DropdownItem,
} from "../shared-components/DropdownCombo";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { CompaniesApi } from "../api/Companies";
import { ExtractTextResult } from "../api/CustomBehaviorPromptsApi";
import { classNames } from "../utils";
import { InputMethod } from "./types";

interface InputConfigurationProps {
  companies: CompaniesApi[];
  companiesLoading: boolean;
  selectedCompanyUuid: string;
  onSelectCompany: (companyUuid: string) => void;
  inputMethod: InputMethod;
  onInputMethodChange: (method: InputMethod) => void;
  sourceText: string;
  onSourceTextChange: (text: string) => void;
  customerIntent: string;
  onCustomerIntentChange: (intent: string) => void;
  sourceFilename: string | undefined;
  onFileSelected: (filename: string, text: string) => void;
  onExtractFile: (file: File) => Promise<ExtractTextResult>;
  onExtractApiError?: (error: unknown) => void;
  isExtracting: boolean;
  onGenerate: () => void;
  isGenerating: boolean;
}

const ACCEPTED = {
  "text/plain": [".txt"],
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
};

const InputConfiguration = ({
  companies,
  companiesLoading,
  selectedCompanyUuid,
  onSelectCompany,
  inputMethod,
  onInputMethodChange,
  sourceText,
  onSourceTextChange,
  customerIntent,
  onCustomerIntentChange,
  sourceFilename,
  onFileSelected,
  onExtractFile,
  onExtractApiError,
  isExtracting,
  onGenerate,
  isGenerating,
}: InputConfigurationProps) => {
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseWarning, setParseWarning] = useState<string | null>(null);
  const selectedCompanyUuidRef = useRef(selectedCompanyUuid);
  const companyItems: DropdownItem[] = companies.map((company) => ({
    id: company.external_id,
    label: company.name,
  }));

  useEffect(() => {
    selectedCompanyUuidRef.current = selectedCompanyUuid;
  }, [selectedCompanyUuid]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0];
      if (!file) return;
      if (!selectedCompanyUuid) {
        setParseError("Select a company before uploading a document.");
        return;
      }
      const requestCompanyUuid = selectedCompanyUuid;
      setParseError(null);
      setParseWarning(null);
      try {
        const result = await onExtractFile(file);
        if (selectedCompanyUuidRef.current !== requestCompanyUuid) return;
        if (!result.source_text.trim()) {
          setParseError(
            "Could not extract readable text from this file. Paste the text manually, or upload a .txt export."
          );
          return;
        }
        onFileSelected(result.source_filename ?? file.name, result.source_text);
        if (result.warnings?.length) {
          setParseWarning(result.warnings.join(" "));
        }
      } catch (e) {
        if (selectedCompanyUuidRef.current !== requestCompanyUuid) return;
        if (axios.isAxiosError(e) && onExtractApiError) {
          onExtractApiError(e);
          return;
        }
        setParseError(
          e instanceof Error && e.message
            ? e.message
            : "Failed to extract text from the uploaded file."
        );
      }
    },
    [onExtractFile, onExtractApiError, onFileSelected, selectedCompanyUuid]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED,
    multiple: false,
    disabled: !selectedCompanyUuid || isExtracting,
  });

  const canGenerate =
    Boolean(selectedCompanyUuid) &&
    sourceText.trim().length > 0 &&
    !isGenerating;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-gray-800">
        1. Input Configuration
      </h2>
      <p className="mt-1 text-sm text-gray-500">
        Pick a company, provide the misconduct guidelines, then generate a
        candidate prompt.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div data-testid="company-select">
          {companiesLoading ? <LoadingSpinner /> : null}
          {!companiesLoading && (
            <DropdownCombo
              items={companyItems}
              initial={
                selectedCompanyUuid
                  ? companyItems.find((item) => item.id === selectedCompanyUuid)
                  : undefined
              }
              disabled={companiesLoading}
              onChange={(item) => {
                if (item.id === selectedCompanyUuid) return;
                onSelectCompany(item.id);
              }}
              label="Customer"
              labelClassName="font-medium text-gray-700 pb-1"
              placeHolder={companiesLoading ? "Loading" : "Select a company"}
              buttonContainerClassName={classNames("w-full")}
              name="selected-company"
              needTypeAhead={true}
            />
          )}
        </div>

        <label className="block">
          <span className="text-sm font-medium text-gray-700 block mb-2">
            Customer intent <span className="text-gray-400">(optional)</span>
          </span>
          <input
            type="text"
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-green-500 focus:outline-none h-10"
            placeholder="e.g. strict on brand integrity and FTC disclosure"
            value={customerIntent}
            onChange={(e) => onCustomerIntentChange(e.target.value)}
          />
        </label>
      </div>

      <div className="mt-5">
        <span className="text-sm font-medium text-gray-700">Input method</span>
        <div className="mt-2 flex gap-2">
          <Button
            text="Paste text"
            colorType={
              inputMethod === "text"
                ? ButtonColorTypes.primary
                : ButtonColorTypes.cancel
            }
            onClick={() => onInputMethodChange("text")}
          />
          <Button
            text="Upload document"
            colorType={
              inputMethod === "document"
                ? ButtonColorTypes.primary
                : ButtonColorTypes.cancel
            }
            onClick={() => onInputMethodChange("document")}
          />
        </div>
      </div>

      {inputMethod === "document" && (
        <div className="mt-4">
          <div
            {...getRootProps()}
            data-testid="document-dropzone"
            className={`w-full rounded-lg border-2 border-dashed border-gray-300 p-6 text-center text-gray-400 ${
              selectedCompanyUuid
                ? "cursor-pointer hover:border-green-400"
                : "cursor-not-allowed opacity-60"
            }`}
          >
            <input {...getInputProps()} />
            <p className="text-sm">
              {!selectedCompanyUuid
                ? "Select a company above before uploading"
                : isExtracting
                ? "Extracting text…"
                : isDragActive
                ? "Drop the handbook here…"
                : "Drag & drop a .txt, .pdf, or .docx, or click to browse"}
            </p>
            {sourceFilename && !isExtracting && (
              <p className="mt-2 text-sm font-medium text-gray-600">
                Loaded: {sourceFilename}
              </p>
            )}
          </div>
          {parseError && (
            <p className="mt-2 text-sm text-red-600" data-testid="parse-error">
              {parseError}
            </p>
          )}
          {parseWarning && (
            <p
              className="mt-2 text-sm text-amber-600"
              data-testid="parse-warning"
            >
              {parseWarning}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-400">
            Text is extracted server-side and shown below for review before
            generating. Scanned PDFs may extract poorly; paste text manually if
            it looks wrong.
          </p>
        </div>
      )}

      <label className="mt-4 block">
        <span className="text-sm font-medium text-gray-700">
          {inputMethod === "document"
            ? "Extracted guidelines"
            : "Misconduct definitions"}
        </span>
        <textarea
          className="mt-1 h-48 w-full rounded-md border border-gray-300 p-2 font-mono text-sm focus:border-green-500 focus:outline-none"
          placeholder="Paste the company's misconduct guidelines or handbook text here…"
          value={sourceText}
          onChange={(e) => onSourceTextChange(e.target.value)}
          data-testid="source-text"
        />
      </label>

      <div className="mt-5">
        <Button
          text={isGenerating ? "Generating…" : "Generate prompt"}
          type={isGenerating ? ButtonTypes.saving : ButtonTypes.default}
          colorType={ButtonColorTypes.primary}
          disabled={!canGenerate}
          onClick={onGenerate}
          data-testid="generate-button"
        />
      </div>
    </section>
  );
};

export default InputConfiguration;
